import api from './api';

export const authService = {
  // Login user
  async login(username, password) {
    try {
      const response = await api.post('/auth/login', { username, password });
      console.log('Login API response:', response.data);
      
      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      // Check for success flag
      if (response.data.success !== true) {
        throw new Error(response.data.message || 'Login failed');
      }
      
      // Validate data structure
      if (!response.data.data || !response.data.data.token || !response.data.data.user) {
        throw new Error('Missing token or user data in response');
      }
      
      // Store token and user in localStorage
      const { token, user } = response.data.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('✅ Token stored in localStorage:', token.substring(0, 20) + '...');
      console.log('✅ User data stored in localStorage:', user);
      console.log('✅ Full response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Login API error:', error);
      
      // Clear any existing auth data on error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Login failed - unknown error');
      }
    }
  },

  // Logout user
  async logout() {
    try {
      await api.post('/auth/logout');
      console.log('✅ Logout API call successful');
    } catch (error) {
      console.warn('⚠️ Logout API call failed, but proceeding with local cleanup:', error.message);
    } finally {
      // Always clean up local storage regardless of API success
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('✅ Local storage cleared');
    }
  },

  // Get current user profile
  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get profile');
    }
  },

  // Update user profile
  async updateProfile(profileData) {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send reset code');
    }
  },

  // Reset password
  async resetPassword(email, code, newPassword) {
    try {
      const response = await api.post('/auth/reset-password', {
        email,
        code,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  // Get current user from localStorage
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Get token
  getToken() {
    return localStorage.getItem('token');
  },

  // Verify stored authentication data
  verifyStoredAuth() {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      console.log('❌ No stored authentication data found');
      return { isAuthenticated: false, token: null, user: null };
    }
    
    try {
      const userData = JSON.parse(user);
      console.log('✅ Stored authentication data verified:', {
        hasToken: !!token,
        tokenPreview: token.substring(0, 20) + '...',
        user: userData
      });
      
      return { 
        isAuthenticated: true, 
        token, 
        user: userData 
      };
    } catch (error) {
      console.error('❌ Error parsing stored user data:', error);
      // Clear corrupted data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { isAuthenticated: false, token: null, user: null };
    }
  },

  // Clear all authentication data
  clearAuth() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('✅ All authentication data cleared');
  },
};
